"use client";

import { useState, useEffect, useRef } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import {
  Plus,
  Edit2,
  Trash2,
  Image as ImageIcon,
  Search,
  Eye,
  AlertTriangle,
  X,
} from "lucide-react";
import toast from "react-hot-toast";
import { createPortal } from "react-dom";
import { getApiClient } from "@/lib/api";
import Pagination from "@/components/Pagination";
import ImageLightbox from "@/components/ImageLightbox";
import AdminPagination from "@/components/pagination/AdminPagination";
import useModalScrollLock from "@/hooks/useModalScrollLock";

const apiClient = getApiClient();

// Zod validation schema
const carouselSchema = z.object({
  title: z
    .string()
    .trim()
    .min(3, "Title must be at least 3 characters.")
    .max(100, "Title cannot exceed 100 characters."),
  subtitle: z
    .string()
    .trim()
    .max(100, "Subtitle cannot exceed 100 characters.")
    .optional()
    .or(z.literal("")),
  description: z
    .string()
    .trim()
    .max(500, "Description cannot exceed 500 characters.")
    .optional()
    .or(z.literal("")),
  buttonText: z.string().trim().optional().or(z.literal("")),
  buttonLink: z
    .string()
    .trim()
    .optional()
    .or(z.literal(""))
    .refine(
      (val) =>
        !val ||
        val.startsWith("/") ||
        val.startsWith("http://") ||
        val.startsWith("https://"),
      {
        message:
          "Please enter a valid URL or path (e.g. /products or https://...)",
      },
    ),
  displayOrder: (z.preprocess(
    (val) => (val === "" || val === null || val === undefined ? undefined : val),
    z.coerce
      .number({ message: "Display Order is required." })
      .min(0, "Display Order cannot be negative.")
  ) as any) as z.ZodNumber,
  status: z.enum(["active", "inactive"]),
  desktopImage: z.instanceof(File).optional(),
});

type CarouselFormData = z.infer<typeof carouselSchema>;

interface Carousel {
  id: string;
  title: string;
  subtitle?: string;
  description?: string;
  desktopImage: string;
  buttonText?: string;
  buttonLink?: string;
  displayOrder: number;
  status: string;
  isPublished: boolean;
  createdAt: string;
}

interface DeleteConfirmProps {
  isOpen: boolean;
  carouselTitle: string;
  isDeleting: boolean;
  onConfirm: () => void;
  onCancel: () => void;
}

function DeleteConfirmModal({
  isOpen,
  carouselTitle,
  isDeleting,
  onConfirm,
  onCancel,
}: DeleteConfirmProps) {
  useModalScrollLock(isOpen);

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-6 max-w-md w-full shadow-xl">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-50 rounded-xl">
            <AlertTriangle className="w-6 h-6 text-red-600" />
          </div>
          <h2 className="text-xl font-bold text-[#6D3A0D]">Delete Carousel</h2>
        </div>

        <p className="text-gray-600 text-sm mb-2">
          Are you sure you want to delete <strong>{carouselTitle}</strong>?
        </p>
        <p className="text-gray-500 text-xs mb-6">
          This action cannot be undone.
        </p>

        <div className="flex gap-3">
          <button
            onClick={onCancel}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-xl hover:bg-[#F6EEE7] transition-colors font-semibold text-sm disabled:opacity-50"
          >
            Cancel
          </button>
          <button
            onClick={onConfirm}
            disabled={isDeleting}
            className="flex-1 px-4 py-2.5 bg-red-600 text-white rounded-xl hover:bg-red-700 transition-colors font-semibold text-sm disabled:opacity-50 flex items-center justify-center gap-2"
          >
            {isDeleting ? (
              <>
                <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                Deleting...
              </>
            ) : (
              "Delete"
            )}
          </button>
        </div>
      </div>
    </div>,
    document.body
  );
}

interface CarouselFormProps {
  isOpen: boolean;
  editingCarousel?: Carousel | null;
  onClose: () => void;
  onSuccess: () => void;
  onPreviewImage?: (src: string, title?: string) => void;
}

function CarouselFormModal({
  isOpen,
  editingCarousel,
  onClose,
  onSuccess,
  onPreviewImage,
}: CarouselFormProps) {
  useModalScrollLock(isOpen);

  const {
    register,
    handleSubmit,
    setValue,
    reset,
    setError,
    clearErrors,
    formState: { errors, isSubmitting },
  } = useForm<CarouselFormData>({
    resolver: zodResolver(carouselSchema),
    defaultValues: {
      title: "",
      subtitle: "",
      description: "",
      buttonText: "",
      buttonLink: "",
      displayOrder: 0,
      status: "active",
    },
  });

  const desktopImageRef = useRef<HTMLInputElement>(null);
  const [desktopPreview, setDesktopPreview] = useState<string | null>(null);
  const [desktopImageFile, setDesktopImageFile] = useState<File | null>(null);
  const [desktopImageRemoved, setDesktopImageRemoved] = useState(false);

  useEffect(() => {
    if (isOpen && editingCarousel) {
      reset({
        title: editingCarousel.title,
        subtitle: editingCarousel.subtitle || "",
        description: editingCarousel.description || "",
        buttonText: editingCarousel.buttonText || "",
        buttonLink: editingCarousel.buttonLink || "",
        displayOrder: editingCarousel.displayOrder,
        status: editingCarousel.status as "active" | "inactive",
      });
      setDesktopPreview(editingCarousel.desktopImage);
      setDesktopImageFile(null);
      setDesktopImageRemoved(false);
    } else if (isOpen) {
      reset({
        title: "",
        subtitle: "",
        description: "",
        buttonText: "",
        buttonLink: "",
        displayOrder: 0,
        status: "active",
      });
      setDesktopPreview(null);
      setDesktopImageFile(null);
      setDesktopImageRemoved(false);
    }
  }, [isOpen, editingCarousel, reset]);

  useEffect(() => {
    const hasExistingImage = !!editingCarousel?.desktopImage;
    const hasNewImage = !!desktopImageFile;
    const imageWillBeRemoved = desktopImageRemoved;
    const willHaveImage =
      (hasExistingImage && !imageWillBeRemoved) || hasNewImage;

    if (!willHaveImage && desktopImageRemoved) {
      setError("desktopImage" as any, {
        type: "manual",
        message: "Desktop image is required.",
      });
    } else {
      clearErrors("desktopImage");
    }
  }, [
    desktopImageFile,
    desktopImageRemoved,
    editingCarousel,
    setError,
    clearErrors,
  ]);

  const handleDesktopImageChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (file) {
      setDesktopImageFile(file);
      setDesktopImageRemoved(false);
      setValue("desktopImage", file);
      clearErrors("desktopImage");
      const reader = new FileReader();
      reader.onload = (e) => {
        setDesktopPreview(e.target?.result as string);
      };
      reader.readAsDataURL(file);
    }
  };

  const removeDesktopImage = () => {
    setDesktopImageFile(null);
    setDesktopImageRemoved(true);
    setValue("desktopImage", undefined);
    setDesktopPreview(null);
    if (desktopImageRef.current) {
      desktopImageRef.current.value = "";
    }
  };

  const onSubmit = async (data: CarouselFormData) => {
    try {
      const hasExistingImage = !!editingCarousel?.desktopImage;
      const hasNewImage = !!desktopImageFile;
      const imageWillBeRemoved = desktopImageRemoved;

      const willHaveImage =
        (hasExistingImage && !imageWillBeRemoved) || hasNewImage;

      if (!willHaveImage) {
        setError("desktopImage" as any, {
          type: "manual",
          message: "Desktop image is required.",
        });
        return;
      }

      const submitData = new FormData();
      submitData.append("title", data.title);
      submitData.append("subtitle", data.subtitle || "");
      submitData.append("description", data.description || "");
      if (desktopImageFile) {
        submitData.append("desktopImage", desktopImageFile);
      } else if (desktopImageRemoved) {
        submitData.append("desktopImage", "");
      }
      submitData.append("buttonText", data.buttonText || "");
      submitData.append("buttonLink", data.buttonLink || "");
      submitData.append("displayOrder", data.displayOrder.toString());
      submitData.append("status", data.status);

      if (editingCarousel) {
        await apiClient.put(
          `/admin/carousels/${editingCarousel.id}`,
          submitData,
          {
            headers: { "Content-Type": "multipart/form-data" },
          },
        );
        toast.success("Carousel updated successfully");
      } else {
        await apiClient.post("/admin/carousels", submitData, {
          headers: { "Content-Type": "multipart/form-data" },
        });
        toast.success("Carousel created successfully");
      }

      onSuccess();
      onClose();
    } catch (error: any) {
      toast.error(error.response?.data?.message || "Failed to save carousel");
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div className="fixed inset-0 bg-black/60 backdrop-blur-xs flex items-center justify-center z-[9999] p-3 sm:p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-5 sm:p-6 max-w-2xl w-full max-h-[90vh] overflow-y-auto shadow-xl">
        <div className="flex items-center justify-between pb-4 mb-4 border-b border-[#F6EEE7]">
          <h2 className="text-xl sm:text-2xl font-bold text-[#6D3A0D]">
            {editingCarousel ? "Edit Carousel" : "Create Carousel"}
          </h2>
          <button
            onClick={onClose}
            disabled={isSubmitting}
            className="p-1 text-gray-400 hover:text-gray-600 rounded-lg hover:bg-gray-100 transition-colors"
          >
            <X size={20} />
          </button>
        </div>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 items-start">
            {/* Title */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Title <span className="text-red-500">*</span>
              </label>
              <input
                {...register("title")}
                type="text"
                placeholder="Carousel title"
                disabled={isSubmitting}
                className={`w-full px-3.5 py-2 sm:py-2.5 border-2 rounded-xl text-sm focus:outline-none disabled:opacity-50 ${
                  errors.title ? "border-red-500" : "border-[#6D3A0D]"
                }`}
              />
              {errors.title && (
                <p className="text-red-500 text-xs mt-1 font-medium">
                  {errors.title.message}
                </p>
              )}
            </div>

            {/* Subtitle */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Subtitle
              </label>
              <input
                {...register("subtitle")}
                type="text"
                placeholder="Optional subtitle"
                disabled={isSubmitting}
                className="w-full px-3.5 py-2 sm:py-2.5 border-2 border-[#6D3A0D] rounded-xl text-sm focus:outline-none disabled:opacity-50"
              />
              {errors.subtitle && (
                <p className="text-red-500 text-xs mt-1 font-medium">
                  {errors.subtitle.message}
                </p>
              )}
            </div>

            {/* Description */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Description
              </label>
              <textarea
                {...register("description")}
                placeholder="Optional description text displayed on the slide"
                disabled={isSubmitting}
                className="w-full px-3.5 py-2 sm:py-2.5 border-2 border-[#6D3A0D] rounded-xl text-sm focus:outline-none disabled:opacity-50 resize-none"
                rows={3}
              />
              {errors.description && (
                <p className="text-red-500 text-xs mt-1 font-medium">
                  {errors.description.message}
                </p>
              )}
            </div>

            {/* Desktop Image */}
            <div className="sm:col-span-2">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Banner Image <span className="text-red-500">*</span>
              </label>
              {desktopPreview ? (
                <div className="mb-2">
                  <div
                    className="relative group rounded-xl overflow-hidden border-2 border-[#F6EEE7] mb-2 bg-gray-950 cursor-pointer"
                    onClick={() =>
                      onPreviewImage &&
                      onPreviewImage(desktopPreview, "Banner Preview")
                    }
                    title="Click to expand preview"
                  >
                    <img
                      src={desktopPreview}
                      alt="Banner Preview"
                      className="w-full h-40 sm:h-48 object-cover group-hover:scale-105 transition-transform"
                    />
                    <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white gap-1.5 font-medium text-xs">
                      <Eye size={18} /> Click to View
                    </div>
                  </div>
                  <button
                    type="button"
                    onClick={removeDesktopImage}
                    disabled={isSubmitting}
                    className="w-full px-3 py-2 border-2 border-red-500 text-red-500 rounded-xl hover:bg-red-50 transition-colors font-semibold text-xs disabled:opacity-50"
                  >
                    Remove Image
                  </button>
                </div>
              ) : (
                <div>
                  <input
                    ref={desktopImageRef}
                    type="file"
                    accept="image/*"
                    onChange={handleDesktopImageChange}
                    disabled={isSubmitting}
                    className={`w-full px-3.5 py-2 border-2 rounded-xl text-sm focus:outline-none disabled:opacity-50 ${
                      errors.desktopImage
                        ? "border-red-500"
                        : "border-[#6D3A0D]"
                    }`}
                  />
                </div>
              )}
              {errors.desktopImage && (
                <p className="text-red-500 text-xs mt-1 font-medium">
                  {errors.desktopImage.message}
                </p>
              )}
            </div>

            {/* Button Text */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Button Text
              </label>
              <input
                {...register("buttonText")}
                type="text"
                placeholder="e.g. Shop Now"
                disabled={isSubmitting}
                className="w-full px-3.5 py-2 border-2 border-[#6D3A0D] rounded-xl text-sm focus:outline-none disabled:opacity-50"
              />
              {errors.buttonText && (
                <p className="text-red-500 text-xs mt-1 font-medium">
                  {errors.buttonText.message}
                </p>
              )}
            </div>

            {/* Button Link */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Button URL
              </label>
              <input
                {...register("buttonLink")}
                type="text"
                placeholder="e.g. /products or https://..."
                disabled={isSubmitting}
                className={`w-full px-3.5 py-2 border-2 rounded-xl text-sm focus:outline-none disabled:opacity-50 ${
                  errors.buttonLink ? "border-red-500" : "border-[#6D3A0D]"
                }`}
              />
              {errors.buttonLink && (
                <p className="text-red-500 text-xs mt-1 font-medium leading-tight">
                  {errors.buttonLink.message}
                </p>
              )}
            </div>

            {/* Display Order */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Display Order <span className="text-red-500">*</span>
              </label>
              <input
                {...register("displayOrder")}
                type="number"
                placeholder="0"
                min="0"
                disabled={isSubmitting}
                className={`w-full px-3.5 py-2 border-2 rounded-xl text-sm focus:outline-none disabled:opacity-50 ${
                  errors.displayOrder ? "border-red-500" : "border-[#6D3A0D]"
                }`}
              />
              {errors.displayOrder && (
                <p className="text-red-500 text-xs mt-1 font-medium leading-tight">
                  {errors.displayOrder.message}
                </p>
              )}
            </div>

            {/* Single Status Control */}
            <div className="col-span-1">
              <label className="block text-xs font-bold text-[#6D3A0D] uppercase tracking-wider mb-1">
                Status
              </label>
              <select
                {...register("status")}
                disabled={isSubmitting}
                className="w-full px-3.5 py-2 border-2 border-[#6D3A0D] rounded-xl text-sm focus:outline-none disabled:opacity-50 bg-white"
              >
                <option value="active">Active</option>
                <option value="inactive">Inactive</option>
              </select>
            </div>
          </div>

          <div className="flex gap-3 pt-4 border-t border-[#F6EEE7]">
            <button
              type="button"
              onClick={onClose}
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 border-2 border-[#6D3A0D] text-[#6D3A0D] rounded-xl hover:bg-[#F6EEE7] transition-colors font-bold text-sm disabled:opacity-50"
            >
              Cancel
            </button>
            <button
              type="submit"
              disabled={isSubmitting}
              className="flex-1 px-4 py-2.5 bg-[#6D3A0D] text-white rounded-xl hover:bg-[#5D2A0D] transition-colors font-bold text-sm disabled:opacity-50 flex items-center justify-center gap-2 shadow-sm"
            >
              {isSubmitting ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-2 border-white border-t-transparent"></div>
                  Saving...
                </>
              ) : editingCarousel ? (
                "Update Carousel"
              ) : (
                "Create Carousel"
              )}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
}

export default function Carousels() {
  const [carousels, setCarousels] = useState<Carousel[]>([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editingCarousel, setEditingCarousel] = useState<Carousel | null>(null);
  const [deleteConfirm, setDeleteConfirm] = useState<{
    id: string;
    title: string;
  } | null>(null);
  const [isDeleting, setIsDeleting] = useState(false);

  // Filter & Pagination States
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("");
  const [page, setPage] = useState(1);
  const [limit, setLimit] = useState(10);
  const [total, setTotal] = useState(0);

  // Lightbox State
  const [lightboxImage, setLightboxImage] = useState<{
    src: string;
    title: string;
  } | null>(null);

  useEffect(() => {
    const timer = setTimeout(() => {
      fetchCarousels();
    }, 300);

    return () => clearTimeout(timer);
  }, [page, limit, statusFilter, search]);

  const fetchCarousels = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams();
      params.append("page", page.toString());
      params.append("limit", limit.toString());
      if (search.trim()) params.append("search", search.trim());
      if (statusFilter) params.append("status", statusFilter);

      const response = await apiClient.get(
        `/admin/carousels?${params.toString()}`,
      );
      const responseData = response.data;

      if (responseData?.data) {
        if (Array.isArray(responseData.data)) {
          setCarousels(responseData.data);
          setTotal(responseData.pagination?.total || responseData.data.length);
        } else if (responseData.data.carousels) {
          setCarousels(responseData.data.carousels);
          setTotal(
            responseData.data.pagination?.total ||
              responseData.pagination?.total ||
              0,
          );
        } else {
          setCarousels([]);
          setTotal(0);
        }
      } else {
        setCarousels([]);
        setTotal(0);
      }
    } catch (error) {
      toast.error("Failed to load carousels");
    } finally {
      setLoading(false);
    }
  };

  const handleSearchSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    setPage(1);
    fetchCarousels();
  };

  const handleEdit = (carousel: Carousel) => {
    setEditingCarousel(carousel);
    setShowForm(true);
  };

  const handleDelete = async () => {
    if (!deleteConfirm) return;

    try {
      setIsDeleting(true);
      await apiClient.delete(`/admin/carousels/${deleteConfirm.id}`);
      toast.success("Carousel deleted successfully");
      setDeleteConfirm(null);
      fetchCarousels();
    } catch (error) {
      toast.error("Failed to delete carousel");
    } finally {
      setIsDeleting(false);
    }
  };

  return (
    <div className="space-y-6 max-w-7xl mx-auto px-2 sm:px-4">
      {/* Header */}
      <div className="flex flex-col sm:flex-row justify-between items-start sm:items-center gap-4">
        <div>
          <h1 className="text-2xl sm:text-3xl font-bold text-[#6D3A0D]">
            Carousels
          </h1>
          <p className="text-xs sm:text-sm text-gray-500 mt-1">
            Manage homepage banner slides & promotions
          </p>
        </div>
        <button
          onClick={() => {
            setEditingCarousel(null);
            setShowForm(true);
          }}
          className="w-full sm:w-auto flex items-center justify-center gap-2 px-4 py-2.5 bg-[#6D3A0D] text-white rounded-xl hover:bg-[#5D2A0D] transition-colors font-bold text-sm shadow-sm"
        >
          <Plus className="w-5 h-5" />
          New Carousel
        </button>
      </div>

      {/* Filter & Search Bar */}
      <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] p-4 shadow-xs">
        <form
          onSubmit={handleSearchSubmit}
          className="flex flex-col sm:flex-row items-center gap-3"
        >
          {/* Search Input */}
          <div className="relative flex-1 w-full">
            <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
            <input
              type="text"
              value={search}
              onChange={(e) => {
                setSearch(e.target.value);
                setPage(1);
              }}
              placeholder="Search carousels by title..."
              className="w-full pl-10 pr-9 py-2 border-2 border-[#F6EEE7] rounded-xl text-sm focus:outline-none focus:border-[#6D3A0D] transition-colors"
            />
            {search && (
              <button
                type="button"
                onClick={() => {
                  setSearch("");
                  setPage(1);
                }}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-gray-400 hover:text-gray-600 p-0.5 rounded-full"
                title="Clear search"
              >
                <X className="w-4 h-4" />
              </button>
            )}
          </div>

          {/* Status Filter */}
          <select
            value={statusFilter}
            onChange={(e) => {
              setStatusFilter(e.target.value);
              setPage(1);
            }}
            className="w-full sm:w-44 px-3.5 py-2 border-2 border-[#F6EEE7] rounded-xl text-sm focus:outline-none focus:border-[#6D3A0D] bg-white font-medium text-gray-700"
          >
            <option value="">All Status</option>
            <option value="active">Active</option>
            <option value="inactive">Inactive</option>
          </select>

          {/* Search Button */}
          <button
            type="submit"
            className="w-full sm:w-auto px-5 py-2 bg-[#6D3A0D] text-white rounded-xl hover:bg-[#5D2A0D] transition-colors font-semibold text-sm whitespace-nowrap"
          >
            Search
          </button>
        </form>
      </div>

      {/* Form Modal */}
      <CarouselFormModal
        isOpen={showForm}
        editingCarousel={editingCarousel}
        onClose={() => {
          setShowForm(false);
          setEditingCarousel(null);
        }}
        onSuccess={fetchCarousels}
        onPreviewImage={(src, title) =>
          setLightboxImage({ src, title: title || "Carousel Image" })
        }
      />

      {/* Delete Confirmation Modal */}
      <DeleteConfirmModal
        isOpen={!!deleteConfirm}
        carouselTitle={deleteConfirm?.title || ""}
        isDeleting={isDeleting}
        onConfirm={handleDelete}
        onCancel={() => setDeleteConfirm(null)}
      />

      {/* Reusable Image Lightbox Modal */}
      <ImageLightbox
        isOpen={!!lightboxImage}
        src={lightboxImage?.src || null}
        alt={lightboxImage?.title}
        onClose={() => setLightboxImage(null)}
      />

      {/* Content List / Table */}
      {loading ? (
        <div className="flex items-center justify-center h-64 bg-white rounded-2xl border-2 border-[#F6EEE7]">
          <div className="animate-spin rounded-full h-10 w-10 border-4 border-[#6D3A0D] border-t-transparent"></div>
        </div>
      ) : carousels.length === 0 ? (
        <div className="bg-white rounded-2xl border-2 border-dashed border-[#F6EEE7] p-8 sm:p-12 text-center">
          <ImageIcon className="w-16 h-16 text-gray-300 mx-auto mb-4" />
          <h3 className="text-lg font-bold text-gray-800 mb-1">
            No carousels found
          </h3>
          <p className="text-gray-500 text-sm mb-6 max-w-md mx-auto">
            {search || statusFilter
              ? "No carousels match your filter criteria."
              : "Create your first carousel to display on the homepage banner carousel."}
          </p>
          <button
            onClick={() => {
              setEditingCarousel(null);
              setShowForm(true);
            }}
            className="px-6 py-2.5 bg-[#6D3A0D] text-white rounded-xl hover:bg-[#5D2A0D] transition-colors font-bold text-sm shadow-sm"
          >
            Create Carousel
          </button>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border-2 border-[#F6EEE7] overflow-hidden shadow-xs">
          {/* Desktop Table View */}
          <div className="hidden md:block overflow-x-auto">
            <table className="w-full text-left border-collapse">
              <thead>
                <tr className="bg-[#F6EEE7]/50 border-b border-[#F6EEE7] text-xs font-bold text-[#6D3A0D] uppercase tracking-wider">
                  <th className="py-3.5 px-4">Banner</th>
                  <th className="py-3.5 px-4">Title & Description</th>
                  <th className="py-3.5 px-4">Order</th>
                  <th className="py-3.5 px-4">Status</th>
                  <th className="py-3.5 px-4 text-right">Actions</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-100 text-sm">
                {carousels.map((carousel) => (
                  <tr
                    key={carousel.id}
                    className="hover:bg-amber-50/30 transition-colors"
                  >
                    {/* Thumbnail Image */}
                    <td className="py-3 px-4">
                      {carousel.desktopImage ? (
                        <div
                          className="relative group w-24 h-14 bg-gray-950 rounded-lg overflow-hidden cursor-pointer border border-gray-200"
                          onClick={() =>
                            setLightboxImage({
                              src: carousel.desktopImage,
                              title: carousel.title,
                            })
                          }
                          title="Click to view full image"
                        >
                          <img
                            src={carousel.desktopImage}
                            alt={carousel.title}
                            className="w-full h-full object-cover group-hover:scale-105 transition-transform"
                          />
                          <div className="absolute inset-0 bg-black/40 opacity-0 group-hover:opacity-100 flex items-center justify-center transition-opacity text-white">
                            <Eye size={16} />
                          </div>
                        </div>
                      ) : (
                        <div className="w-24 h-14 bg-gray-100 rounded-lg flex items-center justify-center text-gray-400">
                          <ImageIcon size={20} />
                        </div>
                      )}
                    </td>

                    {/* Title & Description */}
                    <td className="py-3 px-4 max-w-xs sm:max-w-md">
                      <div className="font-bold text-gray-900 truncate">
                        {carousel.title}
                      </div>
                      {carousel.subtitle && (
                        <div className="text-xs font-medium text-[#6D3A0D] truncate mt-0.5">
                          {carousel.subtitle}
                        </div>
                      )}
                      {carousel.description && (
                        <div
                          className="text-xs text-gray-600 line-clamp-2 mt-1 bg-[#F6EEE7]/40 p-1.5 rounded-lg border border-[#F6EEE7]"
                          title={carousel.description}
                        >
                          <span className="font-semibold text-[#6D3A0D]">Description: </span>
                          {carousel.description}
                        </div>
                      )}
                      {carousel.buttonText && (
                        <span className="inline-block mt-1 text-[11px] font-semibold text-amber-800 bg-amber-50 border border-amber-200 px-2 py-0.5 rounded">
                          Button: {carousel.buttonText}
                        </span>
                      )}
                    </td>

                    {/* Display Order */}
                    <td className="py-3 px-4 font-bold text-gray-700">
                      #{carousel.displayOrder}
                    </td>

                    {/* Status */}
                    <td className="py-3 px-4">
                      <span
                        className={`inline-flex items-center px-2.5 py-1 rounded-full text-xs font-bold ${
                          carousel.status === "active"
                            ? "bg-emerald-100 text-emerald-800 border border-emerald-200"
                            : "bg-gray-100 text-gray-700 border border-gray-200"
                        }`}
                      >
                        {carousel.status === "active" ? "Active" : "Inactive"}
                      </span>
                    </td>

                    {/* Actions */}
                    <td className="py-3 px-4 text-right">
                      <div className="flex items-center justify-end gap-2">
                        <button
                          onClick={() => handleEdit(carousel)}
                          className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors"
                          title="Edit Carousel"
                        >
                          <Edit2 size={16} />
                        </button>
                        <button
                          onClick={() =>
                            setDeleteConfirm({
                              id: carousel.id,
                              title: carousel.title,
                            })
                          }
                          className="p-2 text-red-600 hover:bg-red-50 rounded-lg transition-colors"
                          title="Delete Carousel"
                        >
                          <Trash2 size={16} />
                        </button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Mobile Card List View */}
          <div className="block md:hidden divide-y divide-gray-100">
            {carousels.map((carousel) => (
              <div key={carousel.id} className="p-4 space-y-3">
                <div className="flex items-start justify-between gap-3">
                  <div className="min-w-0 flex-1">
                    <h3 className="font-bold text-gray-900 truncate text-base">
                      {carousel.title}
                    </h3>
                    {carousel.subtitle && (
                      <p className="text-xs font-semibold text-[#6D3A0D] truncate mt-0.5">
                        {carousel.subtitle}
                      </p>
                    )}
                    {carousel.description && (
                      <p className="text-xs text-gray-600 bg-[#F6EEE7]/40 p-2 rounded-lg border border-[#F6EEE7] mt-1.5 line-clamp-2">
                        <span className="font-semibold text-[#6D3A0D]">Description: </span>
                        {carousel.description}
                      </p>
                    )}
                  </div>
                  <span
                    className={`shrink-0 px-2.5 py-0.5 rounded-full text-xs font-bold ${
                      carousel.status === "active"
                        ? "bg-emerald-100 text-emerald-800"
                        : "bg-gray-100 text-gray-700"
                    }`}
                  >
                    {carousel.status === "active" ? "Active" : "Inactive"}
                  </span>
                </div>

                {carousel.desktopImage && (
                  <div
                    className="relative w-full h-36 bg-gray-950 rounded-xl overflow-hidden cursor-pointer"
                    onClick={() =>
                      setLightboxImage({
                        src: carousel.desktopImage,
                        title: carousel.title,
                      })
                    }
                  >
                    <img
                      src={carousel.desktopImage}
                      alt={carousel.title}
                      className="w-full h-full object-cover"
                    />
                    <div className="absolute bottom-2 right-2 bg-black/60 text-white px-2 py-1 rounded text-xs flex items-center gap-1 font-medium">
                      <Eye size={12} /> Preview
                    </div>
                  </div>
                )}

                <div className="flex items-center justify-between pt-2 border-t border-gray-100">
                  <span className="text-xs font-bold text-gray-500">
                    Order: #{carousel.displayOrder}
                  </span>
                  <div className="flex items-center gap-2">
                    <button
                      onClick={() => handleEdit(carousel)}
                      className="px-3 py-1.5 bg-blue-50 text-blue-600 rounded-lg text-xs font-semibold hover:bg-blue-100 transition-colors flex items-center gap-1"
                    >
                      <Edit2 size={14} /> Edit
                    </button>
                    <button
                      onClick={() =>
                        setDeleteConfirm({
                          id: carousel.id,
                          title: carousel.title,
                        })
                      }
                      className="px-3 py-1.5 bg-red-50 text-red-600 rounded-lg text-xs font-semibold hover:bg-red-100 transition-colors flex items-center gap-1"
                    >
                      <Trash2 size={14} /> Delete
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>

          {/* Pagination Footer */}
          <div className="p-4 bg-gray-50 border-t border-gray-100">
            {total > 0 && (
              <AdminPagination
                page={page}
                limit={limit}
                total={total}
                onPageChange={setPage}
                onLimitChange={setLimit}
                isLoading={loading}
                showInfo={true}
              />
            )}
          </div>
        </div>
      )}
    </div>
  );
}
